/**
 * High Contrast Mode Toggle
 *
 * Manages user preference for high contrast mode:
 * - Stores preference in localStorage
 * - Applies/removes .high-contrast class on <html>
 * - Respects OS-level prefers-contrast preference
 * - Updates aria-pressed state on toggle button
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'lws-high-contrast';
  const HTML_CLASS = 'high-contrast';
  const DISABLED_CLASS = 'high-contrast-disabled';

  /**
   * Check if user has OS-level high contrast preference
   */
  function prefersHighContrast() {
    return window.matchMedia('(prefers-contrast: more)').matches;
  }

  /**
   * Get stored user preference
   * Returns: 'enabled', 'disabled', or null (no preference stored)
   */
  function getStoredPreference() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  /**
   * Store user preference
   */
  function setStoredPreference(value) {
    try {
      if (value === null) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, value);
      }
    } catch (e) {
      // localStorage not available
    }
  }

  /**
   * Check if high contrast mode should be active
   */
  function isHighContrastActive() {
    const stored = getStoredPreference();

    // If user has explicitly set a preference, use that
    if (stored === 'enabled') return true;
    if (stored === 'disabled') return false;

    // Otherwise, follow OS preference
    return prefersHighContrast();
  }

  /**
   * Apply high contrast mode to the document
   */
  function applyHighContrast(enabled) {
    const html = document.documentElement;

    if (enabled) {
      html.classList.add(HTML_CLASS);
      html.classList.remove(DISABLED_CLASS);
    } else {
      html.classList.remove(HTML_CLASS);
      // Only add disabled class if user explicitly disabled it
      if (getStoredPreference() === 'disabled') {
        html.classList.add(DISABLED_CLASS);
      }
    }

    // Update all toggle buttons on the page
    updateToggleButtons(enabled);
  }

  /**
   * Update aria-pressed state on all toggle buttons
   */
  function updateToggleButtons(enabled) {
    const buttons = document.querySelectorAll('.high-contrast-toggle');
    buttons.forEach(button => {
      button.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    });
  }

  /**
   * Toggle high contrast mode
   */
  function toggleHighContrast() {
    const currentlyEnabled = document.documentElement.classList.contains(HTML_CLASS);
    const newState = !currentlyEnabled;

    // Store explicit user preference
    setStoredPreference(newState ? 'enabled' : 'disabled');

    // Apply the change
    applyHighContrast(newState);
  }

  /**
   * Initialize toggle button event listeners
   */
  function initToggleButtons() {
    document.addEventListener('click', function(e) {
      const button = e.target.closest('.high-contrast-toggle');
      if (button) {
        e.preventDefault();
        toggleHighContrast();
      }
    });
  }

  /**
   * Listen for OS preference changes
   */
  function listenForOSPreferenceChanges() {
    const mediaQuery = window.matchMedia('(prefers-contrast: more)');

    mediaQuery.addEventListener('change', function(e) {
      // Only auto-apply if user hasn't set an explicit preference
      const stored = getStoredPreference();
      if (stored === null) {
        applyHighContrast(e.matches);
      }
    });
  }

  /**
   * Initialize high contrast mode
   * Called immediately (before DOMContentLoaded) to prevent flash
   */
  function init() {
    // Apply initial state immediately
    const shouldEnable = isHighContrastActive();
    applyHighContrast(shouldEnable);

    // Wait for DOM to set up event listeners
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        initToggleButtons();
        updateToggleButtons(shouldEnable);
      });
    } else {
      initToggleButtons();
      updateToggleButtons(shouldEnable);
    }

    // Listen for OS preference changes
    listenForOSPreferenceChanges();
  }

  // Initialize immediately
  init();

  // Expose toggle function globally for debugging/testing
  window.lwsHighContrast = {
    toggle: toggleHighContrast,
    isActive: function() {
      return document.documentElement.classList.contains(HTML_CLASS);
    },
    enable: function() {
      setStoredPreference('enabled');
      applyHighContrast(true);
    },
    disable: function() {
      setStoredPreference('disabled');
      applyHighContrast(false);
    },
    reset: function() {
      setStoredPreference(null);
      applyHighContrast(prefersHighContrast());
    }
  };
})();
